import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import AppLayout from "@/layouts/AppLayout"
import { fmtElapsedSince, fmtHours } from "@/lib/utils"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconCalendar,
  IconClock,
  IconCopy,
  IconDots,
  IconList,
  IconPencil,
  IconPlayerPlay,
  IconPlayerStop,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react"
import { format } from "date-fns"
import { useCallback, useEffect, useRef, useState } from "react"
import DayCalendar from "./components/DayCalendar"

interface TimeEntry {
  id: number
  user_id: number
  user_name: string
  user_avatar: string | null
  project_id: number | null
  project_name: string
  customer_name: string
  description: string
  entry_date: string
  hours: number | null
  started_at: string | null
  stopped_at: string | null
  billable: boolean
  running: boolean
  timer_display: string
  duration_hours: number | null
}

interface ProjectOption {
  id: number
  name: string
  customer_name: string
}

interface Props {
  [key: string]: unknown
  time_entries: TimeEntry[]
  running_entry: TimeEntry | null
  pagy: PagyMeta | null
  filters: {
    from?: string
    to?: string
    project_id?: string
  }
  projects: ProjectOption[]
  view: "list" | "calendar"
  calendar_date: string | null
  week_start: string | null
}

function formatDate(dateStr: string) {
  const date = new Date(`${dateStr}T00:00:00`)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function formatHours(hours: number | null) {
  if (hours === null) return "—"
  return fmtHours(hours)
}

function RunningTimerDisplay({ entry }: { entry: TimeEntry }) {
  const [elapsed, setElapsed] = useState("")

  useEffect(() => {
    if (!entry.started_at) return

    const startedAt = entry.started_at
    function update() {
      setElapsed(fmtElapsedSince(startedAt))
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [entry.started_at])

  return <span className="font-mono text-sm font-medium">{elapsed}</span>
}

export default function TimeTrackingIndex() {
  const { time_entries, running_entry, pagy, filters, projects, view, calendar_date, week_start } =
    usePage<Props>().props

  const [showManualForm, setShowManualForm] = useState(false)
  const [showTimerForm, setShowTimerForm] = useState(false)
  const [timerProjectId, setTimerProjectId] = useState("")
  const [timerDescription, setTimerDescription] = useState("")
  const [manualProjectId, setManualProjectId] = useState("")
  const [manualDescription, setManualDescription] = useState("")
  const [manualHours, setManualHours] = useState("")
  const [manualDate, setManualDate] = useState(new Date().toISOString().split("T")[0])
  const [manualStartTime, setManualStartTime] = useState("09:00")
  const [manualBillable, setManualBillable] = useState(true)

  // Undo/redo: delete last entry (Ctrl+Z) or re-create it (Ctrl+Shift+Z / Ctrl+Y)
  const MAX_UNDOS = 5
  const busyRef = useRef(false)
  const undoCountRef = useRef(0)
  const redoStackRef = useRef<TimeEntry[]>([])

  const handleUndo = useCallback(() => {
    if (busyRef.current) return
    if (undoCountRef.current >= MAX_UNDOS) return
    const lastEntry = time_entries.find((e) => !e.running)
    if (!lastEntry) return
    busyRef.current = true
    router.delete(`/time_entries/${lastEntry.id}`, {
      onSuccess: () => {
        redoStackRef.current = [...redoStackRef.current, lastEntry]
        undoCountRef.current += 1
      },
      onFinish: () => {
        busyRef.current = false
      },
    })
  }, [time_entries])

  const handleRedo = useCallback(() => {
    if (busyRef.current) return
    const entry = redoStackRef.current[redoStackRef.current.length - 1]
    if (!entry) return
    busyRef.current = true
    router.post(
      "/time_entries",
      {
        time_entry: {
          project_id: entry.project_id || null,
          description: entry.description,
          entry_date: entry.entry_date,
          hours: entry.hours,
          billable: entry.billable,
          started_at: entry.started_at,
          stopped_at: entry.stopped_at,
        },
      },
      {
        onSuccess: () => {
          redoStackRef.current = redoStackRef.current.slice(0, -1)
          undoCountRef.current = Math.max(0, undoCountRef.current - 1)
        },
        onFinish: () => {
          busyRef.current = false
        },
      },
    )
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return

      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault()
        handleUndo()
      } else if (
        ((e.ctrlKey || e.metaKey) && e.key === "z" && e.shiftKey) ||
        ((e.ctrlKey || e.metaKey) && e.key === "y")
      ) {
        e.preventDefault()
        handleRedo()
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [handleUndo, handleRedo])

  const currentView = view || "calendar"

  function buildParams(overrides: Record<string, string | undefined> = {}) {
    const merged: Record<string, string | undefined> = {
      view: currentView,
      from: filters.from,
      to: filters.to,
      project_id: filters.project_id,
      ...(calendar_date ? { date: calendar_date } : {}),
      ...overrides,
    }
    return Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== undefined && v !== ""))
  }

  function switchView(newView: string) {
    router.get("/time_entries", buildParams({ view: newView }), { preserveState: true })
  }

  function applyFilters(overrides: Record<string, string | undefined>) {
    router.get("/time_entries", buildParams(overrides), { preserveState: true })
  }

  function clearFilters() {
    router.get("/time_entries", { view: currentView }, { preserveState: true })
  }

  function handleStartTimer(e: React.FormEvent) {
    e.preventDefault()
    router.post("/time_entries/start", {
      project_id: timerProjectId && timerProjectId !== "none" ? timerProjectId : null,
      description: timerDescription || "Working",
      view: currentView,
    })
    setTimerDescription("")
    setShowTimerForm(false)
  }

  function handleStopTimer() {
    router.patch("/time_entries/stop", { view: currentView })
  }

  function handleManualEntry(e: React.FormEvent) {
    e.preventDefault()
    const hoursNum = Number.parseFloat(manualHours)
    const startedAt = new Date(`${manualDate}T${manualStartTime}:00`)
    const stoppedAt = Number.isFinite(hoursNum) ? new Date(startedAt.getTime() + hoursNum * 3_600_000) : startedAt

    router.post("/time_entries", {
      time_entry: {
        project_id: manualProjectId && manualProjectId !== "none" ? manualProjectId : null,
        description: manualDescription,
        hours: manualHours,
        entry_date: manualDate,
        started_at: startedAt.toISOString(),
        stopped_at: stoppedAt.toISOString(),
        billable: manualBillable,
      },
      view: currentView,
    })
    setManualDescription("")
    setManualHours("")
    setShowManualForm(false)
  }

  function handleDelete(id: number) {
    if (confirm("Delete this time entry?")) {
      router.delete(`/time_entries/${id}`, { data: { view: currentView } })
    }
  }

  function handleDuplicate(id: number) {
    router.post(`/time_entries/${id}/duplicate`, { view: currentView })
  }

  const hasFilters = filters.from || filters.to || filters.project_id

  return (
    <>
      <Head title="Time Tracking - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold">Time Tracking</h1>
            <Tabs value={currentView} onValueChange={switchView}>
              <TabsList>
                <TabsTrigger value="list">
                  <IconList className="mr-1 h-4 w-4" />
                  List
                </TabsTrigger>
                <TabsTrigger value="calendar">
                  <IconCalendar className="mr-1 h-4 w-4" />
                  Calendar
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                router.post("/time_entries/copy_previous_day", {
                  date: format(new Date(), "yyyy-MM-dd"),
                  view: currentView,
                  ...(currentView === "calendar" && week_start ? { date_nav: week_start } : {}),
                })
              }
            >
              <IconCopy className="mr-1 h-4 w-4" />
              Same as yesterday
            </Button>
            {currentView === "list" && (
              <Button size="sm" onClick={() => setShowManualForm(!showManualForm)}>
                <IconPlus className="mr-1 h-4 w-4" />
                Log Time
              </Button>
            )}
            {running_entry ? (
              <div className="bg-muted/50 flex items-center gap-2 rounded-md border px-2 py-1">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                  <IconClock className="h-3 w-3 text-green-600 dark:text-green-400" />
                </div>
                <span className="max-w-[180px] truncate text-sm">
                  {running_entry.project_name}
                  {running_entry.description ? ` · ${running_entry.description}` : ""}
                </span>
                <RunningTimerDisplay entry={running_entry} />
                <Button variant="destructive" size="sm" onClick={handleStopTimer}>
                  <IconPlayerStop className="mr-1 h-4 w-4" />
                  Stop
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={() => setShowTimerForm(!showTimerForm)}>
                <IconPlayerPlay className="mr-1 h-4 w-4" />
                Start Timer
              </Button>
            )}
          </div>
        </div>

        {/* Start-timer form (toggle) */}
        {showTimerForm && !running_entry && (
          <Card>
            <CardContent className="pt-0">
              <form onSubmit={handleStartTimer} className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Project</Label>
                  <Select value={timerProjectId} onValueChange={setTimerProjectId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select project..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Project</SelectItem>
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.customer_name} — {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input
                    placeholder="What are you working on?"
                    value={timerDescription}
                    onChange={(e) => setTimerDescription(e.target.value)}
                  />
                </div>
                <Button variant="ghost" size="sm" type="button" onClick={() => setShowTimerForm(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm">
                  <IconPlayerPlay className="mr-1 h-4 w-4" />
                  Start
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Manual Entry Form (list view only) */}
        {showManualForm && currentView === "list" && (
          <Card>
            <CardContent className="pt-0">
              <form onSubmit={handleManualEntry} className="space-y-4">
                <h3 className="text-sm font-semibold">Log Time Manually</h3>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  <div className="space-y-1">
                    <Label className="text-xs">Project</Label>
                    <Select value={manualProjectId} onValueChange={setManualProjectId}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select project..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Project</SelectItem>
                        {projects.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.customer_name} — {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description</Label>
                    <Input
                      placeholder="What did you work on?"
                      value={manualDescription}
                      onChange={(e) => setManualDescription(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Date</Label>
                    <Input type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Start time</Label>
                    <Input
                      type="time"
                      value={manualStartTime}
                      onChange={(e) => setManualStartTime(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Hours</Label>
                    <Input
                      type="number"
                      step="0.25"
                      min="0.25"
                      placeholder="0.00"
                      value={manualHours}
                      onChange={(e) => setManualHours(e.target.value)}
                      required
                    />
                    {(() => {
                      const h = Number.parseFloat(manualHours)
                      if (!Number.isFinite(h) || h <= 0) return null
                      const [hh, mm] = manualStartTime.split(":").map(Number)
                      const total = hh * 60 + mm + h * 60
                      const endH = Math.floor(total / 60) % 24
                      const endM = Math.round(total % 60)
                      return (
                        <p className="text-muted-foreground text-[11px]">
                          Ends {String(endH).padStart(2, "0")}:{String(endM).padStart(2, "0")}
                        </p>
                      )
                    })()}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={manualBillable}
                      onChange={(e) => setManualBillable(e.target.checked)}
                      className="rounded"
                    />
                    Billable
                  </label>
                  <div className="flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => setShowManualForm(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={!manualHours}>
                    Save Entry
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Calendar View */}
        {currentView === "calendar" && (
          <DayCalendar
            entries={time_entries}
            calendarDate={calendar_date || new Date().toISOString().split("T")[0]}
            weekStart={week_start || calendar_date || new Date().toISOString().split("T")[0]}
            projects={projects}
          />
        )}

        {/* List View */}
        {currentView === "list" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="date"
                value={filters.from || ""}
                onChange={(e) => applyFilters({ from: e.target.value || undefined })}
                className="w-auto"
                placeholder="From"
              />
              <Input
                type="date"
                value={filters.to || ""}
                onChange={(e) => applyFilters({ to: e.target.value || undefined })}
                className="w-auto"
                placeholder="To"
              />
              <Select
                value={filters.project_id || ""}
                onValueChange={(v) => applyFilters({ project_id: v || undefined })}
              >
                <SelectTrigger size="sm">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <IconX className="mr-1 h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>

            {/* Time Entries Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {time_entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-sm">{formatDate(entry.entry_date)}</TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm font-medium">{entry.project_name}</span>
                        <p className="text-muted-foreground text-xs">{entry.customer_name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm">{entry.description}</TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {entry.running ? <RunningTimerDisplay entry={entry} /> : formatHours(entry.hours)}
                    </TableCell>
                    <TableCell>
                      {entry.running ? (
                        <Badge variant="default">Running</Badge>
                      ) : entry.billable ? (
                        <Badge variant="outline">Billable</Badge>
                      ) : (
                        <Badge variant="secondary">Non-billable</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <IconDots className="h-4 w-4" />
                            <span className="sr-only">Actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <a href={`/time_entries/${entry.id}/edit`}>
                              <IconPencil className="mr-2 h-4 w-4" />
                              Edit
                            </a>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(entry.id)}>
                            <IconCopy className="mr-2 h-4 w-4" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem variant="destructive" onClick={() => handleDelete(entry.id)}>
                            <IconTrash className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {time_entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground text-center">
                      {hasFilters ? "No entries match your filters." : "No time entries yet. Start tracking!"}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {pagy && <PagyPagination pagy={pagy} />}
          </>
        )}
      </div>
    </>
  )
}

TimeTrackingIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
